// src/paginas/publico/home/home.tsx
import {
    AccountBalanceWallet,
    CalendarMonth,
    Inventory,
    KeyboardArrowRight,
    ShoppingCart
} from '@mui/icons-material';
import {
    AppBar,
    Box, Button,
    Card,
    CardContent,
    Container,
    Grid,
    Stack,
    Toolbar,
    Typography
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';

const CORES = {
    verde: '#82b07d',
    marinho: '#1d2b4b',
    fundo: '#ffffff',
    cinzaLeve: '#f8fafc'
};

const FeatureCard = ({ icon: Icon, title, description }: any) => (
    <Card sx={{
        height: '100%',
        borderRadius: 5,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            boxShadow: '0 20px 25px -5px rgba(130, 176, 125, 0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            transform: 'translateY(-8px)',
            borderColor: CORES.verde
        }
    }}>
        <CardContent sx={{ p: 4 }}>
            <Box sx={{
                display: 'inline-flex',
                p: 1.5,
                borderRadius: '16px',
                bgcolor: 'rgba(130, 176, 125, 0.1)',
                color: CORES.verde,
                mb: 3
            }}>
                <Icon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: CORES.marinho, mb: 1.5 }}>
                {title}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.7 }}>
                {description}
            </Typography>
        </CardContent>
    </Card>
);

const HomePage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <Box sx={{ bgcolor: CORES.fundo, minHeight: '100vh', overflowX: 'hidden' }}>

            {/* --- NAVBAR --- */}
            <AppBar position="sticky" elevation={0} sx={{
                bgcolor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #e2e8f0'
            }}>
                <Container maxWidth="lg">
                    <Toolbar sx={{ justifyContent: 'space-between', px: '0 !important' }}>
                        <Box
                            component="img"
                            src="/AppCodesh.png"
                            alt="App Codesh"
                            sx={{ height: 38, cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        />
                        <Button
                            variant="contained"
                            onClick={() => navigate('/login')}
                            sx={{
                                bgcolor: CORES.marinho,
                                fontWeight: 700,
                                borderRadius: '10px',
                                px: 3,
                                '&:hover': { bgcolor: '#0f172a' }
                            }}
                        >
                            Acessar Sistema
                        </Button>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* --- HERO SECTION --- */}
            <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
                {/* Decorativo de fundo */}
                <Box sx={{
                    position: 'absolute', top: -150, left: -100, width: 600, height: 600,
                    background: 'radial-gradient(circle, rgba(130,176,125,0.08) 0%, rgba(255,255,255,0) 70%)',
                    zIndex: 0
                }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={8} alignItems="center">
                        <Grid size={{ xs: 12, md: 6.5 }}>
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2, py: 0.5,
                                borderRadius: 10,
                                bgcolor: 'rgba(130, 176, 125, 0.1)',
                                mb: 3
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CORES.verde }} />
                                <Typography variant="caption" sx={{ color: CORES.verde, fontWeight: 700, letterSpacing: 1 }}>
                                    NOVA VERSÃO DISPONÍVEL
                                </Typography>
                            </Box>

                            <Typography variant="h1" sx={{
                                fontSize: { xs: '2.5rem', md: '3.8rem' },
                                fontWeight: 900,
                                lineHeight: 1.1,
                                mb: 3,
                                color: CORES.marinho,
                                letterSpacing: '-0.02em'
                            }}>
                                O ERP que fala a língua do seu <span style={{ color: CORES.verde }}>negócio.</span>
                            </Typography>

                            <Typography variant="h6" sx={{ color: '#475569', mb: 5, fontWeight: 400, lineHeight: 1.8 }}>
                                Controle total da sua operação em um dashboard intuitivo.
                                Integramos <strong>Agenda, Estoque e Financeiro</strong> para você focar no que importa: crescer.
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<KeyboardArrowRight />}
                                    onClick={() => navigate('/cadastro')}
                                    sx={{
                                        px: 5, py: 2, borderRadius: 3, fontSize: '1.1rem', fontWeight: 800,
                                        bgcolor: CORES.verde,
                                        '&:hover': { bgcolor: '#6d9668', transform: 'scale(1.02)' },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Criar Conta Grátis
                                </Button>
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 5.5 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Box sx={{
                                    position: 'absolute',
                                    top: -20,    // O sinal de menos vem antes do valor numérico
                                    left: -20,   // O sinal de menos vem antes do valor numérico
                                    right: 20,
                                    bottom: 20,
                                    border: `2px solid ${CORES.verde}`,
                                    borderRadius: 6,
                                    zIndex: 0
                                }} />
                                <Box
                                    component="img"
                                    src="/home.png"
                                    sx={{ width: '100%', borderRadius: 4, display: 'block' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* --- MÓDULOS --- */}
            <Box sx={{ bgcolor: CORES.cinzaLeve, py: 12 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={10}>
                        <Typography variant="h3" fontWeight={900} sx={{ color: CORES.marinho, mb: 2 }}>
                            Tudo em um só lugar
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', fontSize: '1.1rem' }}>
                            Módulos nativamente integrados para uma gestão sem gaps.
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FeatureCard icon={CalendarMonth} title="Agenda" description="Fusos horários automáticos por unidade e gestão de equipe." />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FeatureCard icon={Inventory} title="Estoque" description="Movimentações entre filiais e baixas automáticas em vendas." />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FeatureCard icon={AccountBalanceWallet} title="Financeiro" description="Controle de fluxo de caixa." />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FeatureCard icon={ShoppingCart} title="PDV" description="Frente de caixa rápida com integração total ao faturamento." />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* --- FOOTER --- */}
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <Container maxWidth="lg">
                    <Stack alignItems="center" spacing={3}>
                        <Box component="img" src="/AppCodesh.png" alt="App Codesh" sx={{ height: 32, filter: 'grayscale(1)', opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            © 2026 App Codesh. A solução definitiva em ERP para serviços e varejo.
                        </Typography>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default HomePage;